/**
 * 運用詳細 — エラータブ
 *
 * エラーフラグの現在状態をカード2カラムGridで表示する。
 * APIから取得したデータをPropsで受け取り、ローディング/エラー/データなしに対応する。
 */

import React from 'react';
import { Card, Row, Col, Tag, Spin, Alert, Empty } from 'antd';
import { GRID_CONFIG, RESPONSIVE_STYLES } from '@/shared/constants/breakpoints';
import { FEEDBACK_BACKGROUND_COLORS, STATUS_COLORS } from '@/shared/constants/colors';
import type { ErrorDisplayItem } from '@/features/panels/constants/panelDetailTab.constants';

// ========== 定数 ==========

const ERROR_TAG_CONFIG = {
  ACTIVE: { color: 'red', text: '発生中' },
  INACTIVE: { color: 'default', text: '—' },
} as const;

// ========== Props ==========

interface ErrorTabProps {
  readonly isMobile: boolean;
  readonly errorDisplayItems: readonly ErrorDisplayItem[];
  readonly isLoadingErrorData: boolean;
  readonly errorDataErrorMessage: string | null;
}

// ========== コンポーネント ==========

export const ErrorTab: React.FC<ErrorTabProps> = ({
  isMobile,
  errorDisplayItems,
  isLoadingErrorData,
  errorDataErrorMessage,
}) => {
  const colProps = isMobile
    ? GRID_CONFIG.detailView.mobile
    : GRID_CONFIG.detailView.desktop;

  const responsiveGutter = isMobile
    ? RESPONSIVE_STYLES.gutter.mobile
    : RESPONSIVE_STYLES.gutter.desktop;

  if (isLoadingErrorData) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip="エラーデータを読み込み中..." />
      </div>
    );
  }

  if (errorDataErrorMessage !== null) {
    return (
      <Alert
        message="データ取得エラー"
        description={errorDataErrorMessage}
        type="warning"
        showIcon
      />
    );
  }

  if (errorDisplayItems.length === 0) {
    return <Empty description="送信データがありません" />;
  }

  return (
    <Row gutter={responsiveGutter}>
      {errorDisplayItems.map((item) => {
        const tagConfig = item.isActive
          ? ERROR_TAG_CONFIG.ACTIVE
          : ERROR_TAG_CONFIG.INACTIVE;

        return (
          <Col key={item.name} {...colProps} className="mb-2">
            <Card
              style={item.isActive ? { backgroundColor: FEEDBACK_BACKGROUND_COLORS.ERROR_LIGHT } : undefined}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span
                    className="font-medium"
                    style={item.isActive ? { color: STATUS_COLORS.ERROR } : undefined}
                  >{item.name}</span>
                  {item.isActive && item.errorCode !== null && (
                    <span className="ml-2 text-xs" style={{ color: '#8c8c8c' }}>
                      エラーコード: {item.errorCode}
                    </span>
                  )}
                </div>
                <Tag color={tagConfig.color}>{tagConfig.text}</Tag>
              </div>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};
